"use client";

import FooterSectionTitle from "@/components/shared/public/fonts/FooterSectionTitle";
import { useTranslations } from "next-intl";
import FooterLink from "./FooterLink";

type Column = {
  titleKey: string;
  links: Array<{ key: string; href: string }>;
};

/**
 * Footer sitemap. Order is intentional — top row of 4, second row of 3
 * (Floor plans / Floor plan types / 360° tour). Driven by ticket #1.
 */
export const ROW_TOP: Column[] = [
  {
    titleKey: "information.title",
    links: [
      { key: "information.link1", href: "/blog/cad-system" },
      { key: "information.link2", href: "/blog/bauzeichnung" },
      { key: "information.link3", href: "/blog/technisches-zeichnen" },
      { key: "information.link4", href: "/blog/aufmass" },
      { key: "information.link5", href: "/blog/aufmass-nehmen" },
      { key: "information.link6", href: "/blog/bestuhlungsform" },
      { key: "information.link7", href: "/blog/bestuhlungsplan" },
      { key: "information.link8", href: "/blog/fengshui" },
      { key: "information.link9", href: "/blog/wohnflaechenberechnung" },
      { key: "information.link10", href: "/blog/wohnflaeche-dachgeschoss" },
      { key: "information.link11", href: "/blog/balkon" },
      { key: "information.link12", href: "/blog/immobilienbewertung" },
      { key: "information.link13", href: "/blog/immobilienversicherung" },
      { key: "information.link14", href: "/blog/umzug" },
      { key: "information.link15", href: "/blog/kaution" },
      { key: "information.link16", href: "/blog/gebaeude-vermessung" },
    ],
  },
  {
    titleKey: "marketing.title",
    links: [
      { key: "marketing.link1", href: "/blog/neubauprojekt" },
      { key: "marketing.link2", href: "/blog/expose" },
      // { key: "marketing.link3", href: "/blog/erfolgsgeschichte" }, — taken down for thin content
      { key: "marketing.link4", href: "/blog/immobilienmarketing" },
      { key: "marketing.link5", href: "/blog/immobilienmakler" },
      { key: "marketing.link6", href: "/blog/home-staging" },
      { key: "marketing.link7", href: "/blog/immobilie-verkaufen" },
      { key: "marketing.link8", href: "/blog/uebergabeprotokoll" },
      { key: "marketing.link9", href: "/blog/immobilieninvestment" },
      { key: "marketing.link10", href: "/blog/immobilienverkauf" },
      { key: "marketing.link11", href: "/blog/teilverkauf" },
      { key: "marketing.link12", href: "/blog/immobilien-vermarkten" },
      { key: "marketing.link13", href: "/blog/factsheet-und-expose" },
    ],
  },
  {
    titleKey: "financing.title",
    links: [
      { key: "financing.link1", href: "/blog/baufinanzierung" },
      { key: "financing.link2", href: "/blog/ablauf-einer-baufinanzierung" },
      {
        key: "financing.link3",
        href: "/blog/unterlagen-fuer-eine-baufinanzierung",
      },
      { key: "financing.link4", href: "/blog/bemasster-grundriss-fuer-bank" },
      {
        key: "financing.link5",
        href: "/blog/nebenkosten-beim-immobilienkauf",
      },
      { key: "financing.link6", href: "/blog/bemasster-grundriss" },
      { key: "financing.link7", href: "/blog/wohnflaeche" },
      { key: "financing.link8", href: "/blog/wohnflaechenberechnung-din277" },
      { key: "financing.link9", href: "/blog/wohnflaechenverordnung" },
    ],
  },
  {
    titleKey: "documents.title",
    links: [
      { key: "documents.link1", href: "/blog/schnitte-und-ansichten" },
      { key: "documents.link2", href: "/blog/bebauungsplan" },
      { key: "documents.link3", href: "/blog/bebauungsplan-lesen" },
      { key: "documents.link4", href: "/blog/grundsteuerreform" },
      { key: "documents.link5", href: "/blog/lageplan" },
      { key: "documents.link6", href: "/blog/energieausweis" },
      { key: "documents.link7", href: "/blog/wohnungsgeberbestaetigung" },
      {
        key: "documents.link8",
        href: "/blog/abgeschlossenheitsbescheinigung",
      },
      { key: "documents.link9", href: "/blog/factsheet" },
      { key: "documents.link10", href: "/blog/grundriss-bild-kombination" },
      { key: "documents.link11", href: "/blog/hotel" },
      { key: "documents.link12", href: "/blog/grundrisse-mit-autocad" },
    ],
  },
];

export const ROW_BOTTOM: Column[] = [
  {
    titleKey: "floorplans.title",
    links: [
      { key: "floorplans.link1", href: "/blog/grundriss" },
      { key: "floorplans.link2", href: "/blog/grundriss-plan" },
      { key: "floorplans.link3", href: "/blog/grundriss-erstellen" },
      { key: "floorplans.link4", href: "/blog/grundriss-erstellen-lassen" },
      { key: "floorplans.link7", href: "/blog/grundriss-zeichnen-per-hand" },
      { key: "floorplans.link8", href: "/blog/grundriss-online-zeichnen" },
      { key: "floorplans.link9", href: "/blog/grundriss-digitalisieren" },
      { key: "floorplans.link10", href: "/blog/haus-zeichnen" },
      { key: "floorplans.link11", href: "/blog/bauplan" },
      { key: "floorplans.link12", href: "/blog/moderner-grundriss" },
      { key: "floorplans.link13", href: "/blog/perfekter-grundriss" },
      { key: "floorplans.link14", href: "/blog/immogrundriss" },
    ],
  },
  {
    titleKey: "floorplanTypes.title",
    links: [
      { key: "floorplanTypes.link1", href: "/blog/buerogrundriss" },
      { key: "floorplanTypes.link2", href: "/blog/hausgrundriss" },
      {
        key: "floorplanTypes.link3",
        href: "/blog/grundriss-fuer-einen-bungalow",
      },
      { key: "floorplanTypes.link4", href: "/blog/wohnungsgrundriss" },
      { key: "floorplanTypes.link5", href: "/blog/grundriss-einer-wohnung" },
      { key: "floorplanTypes.link6", href: "/blog/grundriss-doppelhaus" },
      {
        key: "floorplanTypes.link7",
        href: "/blog/grundriss-mehrfamilienhaus",
      },
      { key: "floorplanTypes.link8", href: "/blog/grundriss-reihenhaus" },
      { key: "floorplanTypes.link9", href: "/blog/grundriss-dachgeschoss" },
      {
        key: "floorplanTypes.link10",
        href: "/blog/grundriss-einlieger-wohnung",
      },
      { key: "floorplanTypes.link11", href: "/blog/grundriss-badezimmer" },
      { key: "floorplanTypes.link12", href: "/blog/grundriss-kueche" },
      {
        key: "floorplanTypes.link13",
        href: "/blog/grundrisse-fuer-ferienimmobilien",
      },
      { key: "floorplanTypes.link14", href: "/blog/grundriss-hotel" },
      { key: "floorplanTypes.link15", href: "/blog/grundriss-einfamilienhaus" },
    ],
  },
  {
    titleKey: "tour.title",
    links: [
      { key: "tour.link1", href: "/blog/information-virtueller-rundgang" },
      {
        key: "tour.link2",
        href: "/schritt-fuer-schritt-anleitung-360-grad-rundgang",
      },
      //{ key: "tour.link3", href: "/beispiele-360-grad-rundgang" },
      { key: "tour.link4", href: "/blog/virtueller-rundgang" },
      { key: "tour.link5", href: "/blog/immobilienvisualisierung" },
      { key: "tour.link6", href: "/360-grad-matterport-service" },
      { key: "tour.link7", href: "/blog/matterportservice" },
    ],
  },
];

const Column = ({
  titleKey,
  links,
  t,
}: Column & { t: (key: string) => string }) => (
  <div className="flex min-w-0 flex-col items-start gap-3">
    <FooterSectionTitle title={t(titleKey)} small />
    {links.map(({ key, href }) => (
      <FooterLink key={key} title={t(key)} href={href} small />
    ))}
  </div>
);

const ALL_ROWS: Column[] = [...ROW_TOP, ...ROW_BOTTOM];

const Backlinks = () => {
  const t = useTranslations("backlinks");

  return (
    <div className="bg-secondary border-t">
      <div className="gs--page-column px-4 py-6">
        <details className="group">
          <summary className="text-ring hover:text-primary inline-flex cursor-pointer list-none items-center gap-2 py-3 text-sm font-semibold [&::-webkit-details-marker]:hidden">
            <span>{t("overviewToggle")}</span>
            <span className="transition-transform group-open:rotate-180">
              ▼
            </span>
          </summary>
          <div className="grid grid-cols-1 gap-10 pt-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7">
            {ALL_ROWS.map((col) => (
              <Column key={col.titleKey} {...col} t={t} />
            ))}
          </div>
        </details>
      </div>
    </div>
  );
};

export default Backlinks;
